import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Filter, LayoutList, List, Download, Upload } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


import { useTranslation } from "@/hooks/useTranslation";
import type { Event } from "@/services/api/events";
import {
  getEventRsvpSettings,
  updateEventRsvpSettings,
  getEventGuests,
  createRSVP,
  updateRSVP,
  deleteRSVP,
  addGuestToRSVP,
  updateGuest,
  deleteGuest,
  exportEventGuestsCsv,
  importEventGuestsCsv,
  downloadBlob,
  type EventRsvpSettings,
  type RSVP,
  type Guest,
  type CreateRSVPPayload,
  type CreateGuestPayload,
} from "@/services/api/eventSettings";
import { updateEvent } from "@/services/api/events";
import { RsvpSettingsAccordion } from "@/components/customer/events/RsvpSettingsAccordion";
import { validateRsvpDeadline } from "@/components/customer/events/rsvpDeadline";
import { AutosaveStatus } from "@/components/common/AutosaveStatus";
import { readAutosaveDraft, useAutosave } from "@/hooks/useAutosave";
import { usePendingSaves, waitForPendingSaves } from "@/hooks/autosaveRegistry";
import { GuestSummaryCards } from "@/components/customer/events/GuestSummaryCards";
import { GuestList } from "@/components/customer/events/GuestList";
import { RsvpFunnelCard } from "@/components/customer/events/RsvpFunnelCard";
import { RsvpModal } from "@/components/customer/events/RsvpModal";
import { GuestModal } from "@/components/customer/events/GuestModal";
import { GuestShortLinks } from "@/components/customer/events/GuestShortLinks";
import { useEventShortLinks } from "@/hooks/useEventShortLinks";

interface GuestsTabProps {
  event: Event;
  /** JUB-152: the event as the server acknowledged it, without a reload. */
  onEventPatched?: (event: Event) => void;
}

/** RSVP settings keys this tab autosaves (JUB-152). */
const RSVP_KEYS = ["rsvpEnabled", "kidsEnabled", "accommodationEnabled", "emailNotifications", "dueDate", "mealOptions"] as const;
const RSVP_FORM_ID = "rsvp-settings";
const CUTOFF_FORM_ID = "rsvp-cutoff";
type CutoffPayload = { rsvpCutoffDate: string | null };

/** datetime-local value ("YYYY-MM-DDTHH:mm") ⇄ wall-clock payload, verbatim (no UTC shift). */
const cutoffInputValue = (iso: string | null | undefined) => (iso ? iso.slice(0, 16) : "");
const serializeCutoff = (value: string): CutoffPayload => ({ rsvpCutoffDate: value ? `${value}:00` : null });

function pickRsvp(settings: EventRsvpSettings): EventRsvpSettings {
  return {
    rsvpEnabled: settings.rsvpEnabled,
    kidsEnabled: settings.kidsEnabled,
    accommodationEnabled: settings.accommodationEnabled,
    emailNotifications: settings.emailNotifications,
    dueDate: settings.dueDate ?? null,
    mealOptions: {
      standard: settings.mealOptions?.standard ?? false,
      vegan: settings.mealOptions?.vegan ?? false,
      vegetarian: settings.mealOptions?.vegetarian ?? false,
    },
  };
}

export function GuestsTab({ event, onEventPatched }: GuestsTabProps) {
  const { t, currentLanguage } = useTranslation();
  const [settings, setSettings] = useState<EventRsvpSettings | null>(null);
  const [rsvpInitial, setRsvpInitial] = useState<EventRsvpSettings | null>(null);
  const [rsvpLoadTick, setRsvpLoadTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "confirmed" | "pending" | "declined">("all");
  const [viewMode, setViewMode] = useState<"rsvp" | "guests">("rsvp");
  // JUB-51: trackable short links, loaded once and shown per guest (RSVP) row.
  const shortLinks = useEventShortLinks(event.uuid);
  
  // Modal states
  const [rsvpModalOpen, setRsvpModalOpen] = useState(false);
  const [rsvpModalMode, setRsvpModalMode] = useState<"create" | "edit">("create");
  const [selectedRSVP, setSelectedRSVP] = useState<RSVP | undefined>();
  const [rsvpLoading, setRsvpLoading] = useState(false);
  
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestModalMode, setGuestModalMode] = useState<"create" | "edit">("create");
  const [selectedGuest, setSelectedGuest] = useState<{ rsvp: RSVP; guest: Guest } | undefined>();
  const [guestLoading, setGuestLoading] = useState(false);
  
  // Auto-expand RSVP after creation to show first guest for editing
  const [expandRSVPId, setExpandRSVPId] = useState<string | undefined>();
  
  // Delete confirmation states
  const [deleteRSVPId, setDeleteRSVPId] = useState<string | null>(null);
  const [deleteGuestId, setDeleteGuestId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      setRsvpInitial(null);
      try {
        // JUB-152: an autosave from an earlier visit may still be in flight.
        await waitForPendingSaves(event.uuid);
        const [settingsData, guestsData] = await Promise.all([
          getEventRsvpSettings(event.uuid),
          getEventGuests(event.uuid).catch(() => []), // Fallback to empty array if guests endpoint not ready
        ]);
        if (!isMounted) return;
        const server = pickRsvp(settingsData);
        const draft = readAutosaveDraft(event.uuid, RSVP_FORM_ID) as Partial<EventRsvpSettings> | undefined;
        setSettings(draft ? { ...server, ...draft, mealOptions: { ...server.mealOptions, ...(draft.mealOptions ?? {}) } } : server);
        setRsvpInitial(server);
        // Ensure guestsData is always an array
        setRsvps(Array.isArray(guestsData) ? guestsData : []);
      } catch (error) {
        console.error(error);
        toast.error(t("common.error"));
        // Ensure rsvps is always an array even on error
        if (isMounted) {
          setRsvps([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, [event.uuid, t, rsvpLoadTick]);

  // ------------------------------------------------------------ autosave (JUB-152)
  const rsvpCurrent = useMemo(() => (settings ? pickRsvp(settings) : null), [settings]);
  const validateRsvp = useCallback((p: EventRsvpSettings): Record<string, string> => {
    const error = validateRsvpDeadline(p.dueDate, event.date, t);
    return error ? { dueDate: error } : {};
  }, [event.date, t]);
  const rsvpAutosave = useAutosave<EventRsvpSettings, EventRsvpSettings>({
    current: rsvpCurrent ?? ({} as EventRsvpSettings),
    initial: rsvpInitial,
    resetKey: `${event.uuid}:${rsvpLoadTick}`,
    pendingKey: event.uuid,
    formId: RSVP_FORM_ID,
    allowedKeys: RSVP_KEYS,
    validate: validateRsvp,
    save: (patch) => updateEventRsvpSettings(event.uuid, patch),
  });

  // RSVP cutoff: saved as typed (debounced) and on blur — no page reload.
  const cutoffSaveInFlight = usePendingSaves(event.uuid);
  const [cutoffValue, setCutoffValue] = useState(() => cutoffInputValue(event.rsvpCutoffDate));
  const [cutoffInitial, setCutoffInitial] = useState<CutoffPayload | null>(null);
  const [cutoffSeed, setCutoffSeed] = useState(0);
  useEffect(() => {
    if (cutoffInitial || cutoffSaveInFlight) return;
    const server = cutoffInputValue(event.rsvpCutoffDate);
    const draft = readAutosaveDraft(event.uuid, CUTOFF_FORM_ID) as Partial<CutoffPayload> | undefined;
    setCutoffValue(draft && "rsvpCutoffDate" in draft ? cutoffInputValue(draft.rsvpCutoffDate) : server);
    setCutoffInitial(serializeCutoff(server));
  }, [cutoffInitial, cutoffSaveInFlight, event.uuid, event.rsvpCutoffDate, cutoffSeed]);
  const cutoffAutosave = useAutosave<CutoffPayload, Event>({
    current: serializeCutoff(cutoffValue),
    initial: cutoffInitial,
    resetKey: `${event.uuid}:${cutoffSeed}`,
    pendingKey: event.uuid,
    formId: CUTOFF_FORM_ID,
    allowedKeys: ["rsvpCutoffDate"],
    save: (patch) => updateEvent(event.uuid, patch),
    onSaved: (updated) => {
      onEventPatched?.(updated);
      const syncedDueDate = updated.rsvpCutoffDate ? updated.rsvpCutoffDate.slice(0, 10) : null;
      setSettings((prev) => (prev ? { ...prev, dueDate: syncedDueDate } : prev));
      setRsvpInitial((prev) => (prev ? { ...prev, dueDate: syncedDueDate } : prev));
    },
  });
  const discardCutoffDraft = () => {
    cutoffAutosave.discardDraft();
    setCutoffInitial(null);
    setCutoffSeed((n) => n + 1);
  };
  const discardRsvpDraft = () => {
    rsvpAutosave.discardDraft();
    setRsvpLoadTick((n) => n + 1);
  };

  const loadGuests = async () => {
    try {
      const guestsData = await getEventGuests(event.uuid);
      
      const rsvpsArray = Array.isArray(guestsData) ? guestsData : [];
      setRsvps(rsvpsArray);
    } catch (error) {
      console.error("Error loading guests:", error);
      setRsvps([]);
    }
  };


  const handleCreateRSVP = () => {
    setRsvpModalMode("create");
    setSelectedRSVP(undefined);
    setRsvpModalOpen(true);
  };

  const handleEditRSVP = (rsvp: RSVP) => {
    setRsvpModalMode("edit");
    setSelectedRSVP(rsvp);
    setRsvpModalOpen(true);
  };

  const handleRSVPSubmit = async (payload: CreateRSVPPayload | { id: string; payload: Partial<CreateRSVPPayload> }) => {
    setRsvpLoading(true);
    let newlyCreatedRSVP: RSVP | null = null;
    try {
      if ("id" in payload) {
        // Edit mode
        await updateRSVP(event.uuid, payload.id, payload.payload);
        toast.success(t("guests.rsvpUpdated"));
      } else {
        // Create mode
        newlyCreatedRSVP = await createRSVP(event.uuid, payload);
        toast.success(t("guests.rsvpCreated"));
      }
      // Close modal first
      setRsvpModalOpen(false);
      // Small delay to ensure backend has committed the transaction
      await new Promise(resolve => setTimeout(resolve, 300));
      // Reload guests list to show the new/updated RSVP
      await loadGuests();

      // JUB-85: no auto-opened edit modal after creation (it forced a dismissal on
      // every single entry during bulk data entry). Just expand the new family
      // inline so the organizer sees what was created.
      if (newlyCreatedRSVP && !("id" in payload)) {
        setExpandRSVPId(newlyCreatedRSVP.id);
        setTimeout(() => setExpandRSVPId(undefined), 1000);
      }
    } catch (error: unknown) {
      console.error("Error in handleRSVPSubmit:", error);
      toast.error((error as Error)?.message || t("common.error"));
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleDeleteRSVP = async () => {
    if (!deleteRSVPId) return;
    setDeleting(true);
    try {
      await deleteRSVP(event.uuid, deleteRSVPId);
      toast.success(t("guests.deleteRsvpSuccess"));
      await loadGuests();
      setDeleteRSVPId(null);
    } catch (error: unknown) {
      console.error(error);
      toast.error((error as Error)?.message || t("guests.deleteRsvpError"));
    } finally {
      setDeleting(false);
    }
  };

  const handleAddGuest = (rsvp: RSVP) => {
    setGuestModalMode("create");
    setSelectedGuest({ rsvp, guest: {} as Guest });
    setGuestModalOpen(true);
  };

  const handleEditGuest = (rsvp: RSVP, guest: Guest) => {
    setGuestModalMode("edit");
    setSelectedGuest({ rsvp, guest });
    setGuestModalOpen(true);
  };

  const handleGuestSubmit = async (payload: CreateGuestPayload | { id: string; payload: Partial<CreateGuestPayload> }) => {
    setGuestLoading(true);
    try {
      if ("id" in payload) {
        // Edit mode
        await updateGuest(event.uuid, payload.id, payload.payload);
        toast.success(t("guests.guestUpdated"));
      } else {
        // Create mode - need to find the RSVP ID
        if (!selectedGuest?.rsvp.id) {
          throw new Error("RSVP ID not found");
        }
        await addGuestToRSVP(event.uuid, selectedGuest.rsvp.id, payload);
        toast.success(t("guests.guestAdded"));
      }
      await loadGuests();
    } catch (error: unknown) {
      console.error(error);
      toast.error((error as Error)?.message || t("common.error"));
    } finally {
      setGuestLoading(false);
    }
  };

  const handleDeleteGuest = async () => {
    if (!deleteGuestId) return;
    setDeleting(true);
    try {
      await deleteGuest(event.uuid, deleteGuestId);
      toast.success(t("guests.deleteGuestSuccess"));
      await loadGuests();
      setDeleteGuestId(null);
    } catch (error: unknown) {
      console.error(error);
      toast.error((error as Error)?.message || t("guests.deleteGuestError"));
    } finally {
      setDeleting(false);
    }
  };

  // JUB-89: download the guest list as CSV through the authenticated client
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const lang = currentLanguage?.toLowerCase().startsWith("en") ? "en" : "ro";
      const { blob, filename } = await exportEventGuestsCsv(event.uuid, lang);
      downloadBlob(blob, filename || `${event.url || event.uuid}-invitati.csv`);
      toast.success(t("guests.exportCsvSuccess"));
    } catch (error: unknown) {
      console.error(error);
      toast.error(t("guests.exportCsvError"));
    } finally {
      setExporting(false);
    }
  };

  // JUB-225: bulk-import guests from CSV
  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const { imported } = await importEventGuestsCsv(event.uuid, file);
      const refreshed = await getEventGuests(event.uuid);
      setRsvps(Array.isArray(refreshed) ? refreshed : []);
      toast.success(t("guests.importCsvSuccess", { count: String(imported) }));
    } catch (error: unknown) {
      console.error(error);
      toast.error((error as Error)?.message || t("guests.importCsvError"));
    } finally {
      setImporting(false);
    }
  };

  if (loading || !settings) {
    return (
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b">
          <CardTitle className="text-2xl">{t("events.form.guests")}</CardTitle>
        </CardHeader>
        <CardContent className="py-12 text-center text-muted-foreground">{t("common.loading")}</CardContent>
      </Card>
    );
  }

  // Ensure rsvps is always an array before using array methods
  const safeRsvps = Array.isArray(rsvps) ? rsvps : [];
  const totalGuests = safeRsvps.reduce((sum, rsvp) => sum + (Array.isArray(rsvp.guests) ? rsvp.guests.length : 0), 0);
  const confirmedCount = safeRsvps.filter((rsvp) => rsvp.status === "confirmed").length;

  return (
    <div className="space-y-6">
      {/* Top section with the title and the actions; stacked on phones (JUB-208) */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">
            {t("events.form.guestManagement")}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {safeRsvps.length} {t("events.form.rsvps")} • {totalGuests}{" "}
            {t("events.form.totalGuests")} • {confirmedCount}{" "}
            {t("events.form.confirmedFamilies")}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2 sm:justify-end">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportCsv}
            className="hidden"
            data-testid="import-guests-csv-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-guests-csv-button"
          >
            <Upload className="mr-2 h-4 w-4" />
            {importing ? t("guests.importCsvInProgress") : t("guests.importCsv")}
          </Button>
          {/* JUB-89: CSV export */}
          <div className="flex flex-col items-start gap-1 sm:items-end">
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={exporting || totalGuests === 0}
            title={totalGuests === 0 ? t("guests.exportCsvEmpty") : undefined}
            data-testid="export-guests-csv-button"
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? t("guests.exportCsvInProgress") : t("guests.exportCsv")}
          </Button>
          {totalGuests === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="export-guests-csv-empty-hint">
              {t("guests.exportCsvEmpty")}
            </p>
          )}
          </div>
          <Button
            onClick={handleCreateRSVP}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="add-rsvp-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("events.form.addRSVP")}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <GuestSummaryCards rsvps={safeRsvps} />

      {/* RSVP Settings Accordion */}
      <RsvpSettingsAccordion
        eventDate={event.date}
        settings={settings}
        showDeadlineField={false}
        onChange={(next) => {
          // Switches save right away; the deadline field is debounced.
          if (!rsvpCurrent || next.dueDate === rsvpCurrent.dueDate) rsvpAutosave.flushOnNextChange();
          setSettings(next);
        }}
        status={rsvpAutosave.status}
        restored={rsvpAutosave.restored}
        onRetry={() => void rsvpAutosave.retry()}
        onDiscard={discardRsvpDraft}
        onSaveNow={() => void rsvpAutosave.flush()}
      />

      {/* RSVP Cutoff Date */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t("events.form.rsvpCutoffDate")}
          </CardTitle>
          <CardDescription>
            {t("events.form.rsvpCutoffDateDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Input
              type="datetime-local"
              data-testid="rsvp-cutoff-input"
              value={cutoffValue}
              disabled={!cutoffInitial}
              // Wall-clock verbatim — toISOString() shifted the deadline by the
              // local UTC offset on both write and read (same bug as ceremonies).
              onChange={(e) => setCutoffValue(e.target.value)}
              onBlur={() => void cutoffAutosave.flush()}
            />
            <AutosaveStatus
              className="justify-start"
              status={cutoffAutosave.status}
              restored={cutoffAutosave.restored}
              onRetry={() => void cutoffAutosave.retry()}
              onDiscard={discardCutoffDraft}
            />
            {event.rsvpCutoffDate && (
              <p className="text-xs text-muted-foreground">
                {t("guests.rsvpCutoffDateHint", { // JUB-23: fix broken i18n key
                  date: new Date(event.rsvpCutoffDate).toLocaleString(currentLanguage === "en" ? "en-GB" : "ro-RO"),
                })}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* JUB-67: RSVP-form interaction counts */}
      <RsvpFunnelCard eventUuid={event.uuid} />

      {/* RSVPs & Guests Section */}
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b">
          <CardTitle className="text-2xl">
            {t("events.form.rsvpsAndGuests")}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {/* Header with View Toggle, Search and Filter - matches reference design */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left side: Title and View Toggle */}
            <div className="flex items-center gap-3">
              {/* View Mode Toggle - matches reference design */}
              <div className="flex items-center bg-muted rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setViewMode("rsvp")}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md h-7 px-2 text-xs ${
                    viewMode === "rsvp"
                      ? "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <LayoutList className="h-3.5 w-3.5 mr-1" />
                  {t("guests.rsvpView")}
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("guests")}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-md h-7 px-2 text-xs ${
                    viewMode === "guests"
                      ? "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <List className="h-3.5 w-3.5 mr-1" />
                  {t("guests.guestView")}
                </button>
              </div>
            </div>

            {/* Right side: Search and Filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 min-w-0 sm:flex-none sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("events.form.searchRSVPsOrGuests")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <Select value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
                <SelectTrigger className="w-[130px] h-9">
                  <Filter className="mr-2 h-4 w-4" />
                  {/* explicit child: SelectValue renders the raw value ("all")
                      before items mount — same class as the status-select fix */}
                  <SelectValue>
                    {filter === "all" ? t("common.all") : t(`events.form.status.${filter}`)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  <SelectItem value="confirmed">
                    {t("events.form.status.confirmed")}
                  </SelectItem>
                  <SelectItem value="pending">
                    {t("events.form.status.pending")}
                  </SelectItem>
                  <SelectItem value="declined">
                    {t("events.form.status.declined")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Guest List */}
          <GuestList
            rsvps={safeRsvps}
            searchQuery={searchQuery}
            filter={filter}
            viewMode={viewMode}
            onEditRSVP={handleEditRSVP}
            onDeleteRSVP={(rsvp) => setDeleteRSVPId(rsvp.id)}
            onEditGuest={handleEditGuest}
            onDeleteGuest={(_rsvp, guest) => setDeleteGuestId(guest.id)}
            onAddGuest={handleAddGuest}
            expandRSVPId={expandRSVPId}
            renderRsvpExtras={(rsvp) => (
              <GuestShortLinks
                guestUuid={rsvp.id}
                guestName={rsvp.familyName || rsvp.email || ""}
                state={shortLinks}
              />
            )}
          />
        </CardContent>
      </Card>

      {/* RSVP Modal */}
      <RsvpModal
        open={rsvpModalOpen}
        mode={rsvpModalMode}
        rsvp={selectedRSVP}
        onOpenChange={setRsvpModalOpen}
        onSubmit={handleRSVPSubmit}
        loading={rsvpLoading}
      />

      {/* Guest Modal */}
      <GuestModal
        open={guestModalOpen}
        mode={guestModalMode}
        guest={selectedGuest?.guest}
        onOpenChange={setGuestModalOpen}
        onSubmit={handleGuestSubmit}
        loading={guestLoading}
      />

      {/* Delete RSVP Confirmation */}
      <AlertDialog open={!!deleteRSVPId} onOpenChange={(open) => !open && setDeleteRSVPId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("guests.deleteRsvpConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("guests.deleteRsvpConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRSVP} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? t("common.loading") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Guest Confirmation */}
      <AlertDialog open={!!deleteGuestId} onOpenChange={(open) => !open && setDeleteGuestId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("guests.deleteGuestConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("guests.deleteGuestConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGuest} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? t("common.loading") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


